import React, {useEffect} from 'react';
import DialogMUI from '../../../shared/DialogMUI';
import ButtonMUI from '../../../shared/ButtonMUI';
import {Controller, useForm} from 'react-hook-form';
import {useTranslation} from 'react-i18next';
import {yupResolver} from '@hookform/resolvers/yup';
import * as yup from 'yup';
import {closeModal} from '../../../redux/actions/app/appActions';
import {POPUPS} from '../../../redux/actions/app/appActionTypes';
import {useDispatch, useSelector} from 'react-redux';
import InputMUI from '../../../shared/InputMUI';
import '../Profile.scss';
import {changeUserPassword, editUser} from '../../../redux/actions/profile/profileActions';
import {notifySuccess} from '../../../helpers/notifySnack';
import {clearEmployee} from '../../../redux/actions/employee/employeeActions';
import passIcon from '../../../assets/icons/pass.svg';
import {useLocation} from 'react-router-dom';
import {clearClients} from '../../../redux/actions/clients/clientsActions';
import {resetProjectParticipants} from '../../../redux/actions/projects/projectsActions';
import {passwordRegex} from "../../../helpers/constants";

const UserEdit = () => {
    const dispatch = useDispatch();
    const location = useLocation();
    const {t} = useTranslation();
    const popupToShow = useSelector(({app}) => app.popupToShow);
    const popupData = useSelector(({app}) => app.popupToShowData);
    const {singleCompany} = useSelector((state) => state.company);
    const {singleProject} = useSelector((state) => state.projects);
    const schema = yup.object().shape({
        name: yup.string().required(t('validations.required')),
        phone: yup.string().required(t('validations.required')),
        email: yup.string().email(t('CompaniesEditing.email_invalid')).required(t('validations.required')),
        newPassword: yup
            .string()
            .transform(value => (value === '' ? null : value)) // Convert empty string to null
            .nullable()
            .notRequired()
            .matches(passwordRegex, t('validations.password'))
    });

    const {
        control,
        handleSubmit,
        reset,
        formState: {errors, isSubmitting, isValid}
    } = useForm({
        mode: 'all',
        reValidateMode: 'onChange',
        shouldFocusError: true,
        resolver: yupResolver(schema),
        defaultValues: {
            name: '',
            role: '',
            phone: '',
            email: '',
            newPassword: ''
        }
    });

    const onCloseHandler = () => {
        dispatch(closeModal());
        setTimeout(() => {
            reset({
                name: '',
                role: '',
                phone: '',
                email: '',
                newPassword: ''
            });
        }, 150);
    };
    const popupVisible = POPUPS.EDIT_USER === popupToShow;

    useEffect(() => {
        if (popupVisible && Object.keys(popupData).length) {
            reset({
                id: popupData.id,
                name: popupData.name,
                phone: popupData.phone,
                email: popupData.email,
                newPassword: ''
            });
        }
    }, [popupVisible]);

    const onSubmit = async (data) => {
        try {
            const formData = new FormData();
            const formDataPassword = new FormData();
            formData.append('name', data.name);
            formData.append('phone', data.phone);
            formData.append('email', data.email);
            const res = await dispatch(editUser(popupData.id, formData));

            if (data.newPassword?.length) {
                formDataPassword.append('password', data.newPassword);
                formDataPassword.append('password_confirmation', data.newPassword);
                await dispatch(changeUserPassword(popupData.id, formDataPassword));
            }
            if (res.payload) {
                if (Object.keys(singleCompany).length) {
                    if (location.pathname.includes('companies-employees')) {
                        dispatch(clearEmployee());
                    }
                    if (location.pathname.includes('companies-customers')) {
                        dispatch(clearClients());
                    }

                    notifySuccess(t('all.successfully'));
                    onCloseHandler();
                }
                if (Object.keys(singleProject).length) {
                    if (location.pathname.includes('projects-participants')) {
                        dispatch(resetProjectParticipants());
                    }
                    notifySuccess(t('all.successfully'));
                    onCloseHandler();
                }
            }
        } finally {
        }
    };

    return (
        <DialogMUI maxWidth={'sm'} open={popupVisible} onClose={onCloseHandler} title={t('all.editUser')}>
            <div className='user-details__form'>
                <div className='user_edit_input'>
                    <Controller
                        name='name'
                        control={control}
                        render={({field}) => (
                            <InputMUI
                                type='text'
                                fullWidth
                                className='no-icons'
                                error={errors.name?.message}
                                inputProps={field}
                                onChange={field.onChange}
                                value={field.value}
                                label={t('profileDetails.lastNameFirstName')}
                            />
                        )}
                    />
                </div>

                <div className='user_edit_input'>
                    <Controller
                        name='phone'
                        control={control}
                        render={({field, fieldState: {error}}) => (
                            <InputMUI
                                className='no-icons'
                                type='tel'
                                fullWidth
                                onChange={(e) => {
                                    const value = e.target.value;

                                    let cleanValue = value.replace(/[^0-9+]/g, ''); // Remove anything that's not a digit or '+'

                                    if (cleanValue.charAt(0) !== '+') {
                                        cleanValue = cleanValue.replace(/\+/g, ''); // Remove any '+' that is not at the start
                                    } else {
                                        cleanValue = '+' + cleanValue.replace(/\+/g, ''); // Keep only the first '+' and remove others
                                    }

                                    if (/^\+?\d*$/.test(cleanValue)) {
                                        field.onChange(cleanValue);
                                    }
                                }}
                                value={field.value}
                                error={error?.message}
                                label={t('profileDetails.phone')}
                                inputProps={field}
                            />
                        )}
                    />
                </div>

                <div className='user_edit_input'>
                    <Controller
                        name='email'
                        control={control}
                        render={({field}) => (
                            <InputMUI
                                type='email'
                                fullWidth
                                onChange={field.onChange}
                                value={field.value}
                                className='no-icons'
                                error={errors.email?.message}
                                inputProps={field}
                                label={t('profileDetails.email')}
                            />
                        )}
                    />
                </div>
                <div className='form__input'>
                    <Controller
                        name='newPassword'
                        control={control}
                        render={({field}) => (
                            <InputMUI
                                type='password'
                                fullWidth
                                error={errors.newPassword?.message}
                                inputProps={{
                                    ...field,
                                    role: 'presentation',
                                    autoComplete: 'new-password',
                                }}
                                label={t('profileSettings.newPassword')}
                                startAdornment={passIcon}
                            />
                        )}
                    />
                </div>
                <ButtonMUI
                    disabled={!isValid || isSubmitting}
                    loading={isSubmitting}
                    onClick={handleSubmit(onSubmit)}
                    fullWidth={true}
                >
                    {t('EmployeesInner.change')}
                </ButtonMUI>
            </div>
        </DialogMUI>
    );
};

export default UserEdit;
